import { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import "./orders.css";
import {
  GridColDef,
  GridRenderCellParams,
  GridRowSelectionModel,
} from "@mui/x-data-grid";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import FilterAltOffOutlinedIcon from "@mui/icons-material/FilterAltOffOutlined";
import { ClearAllOutlined } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import OrderTableClientCell from "../../../../../components/order-table/client-cell";
import OrderTableFlagsCell from "../../../../../components/order-table/flags-cell";
import OrderTableOrderIdCell from "../../../../../components/order-table/orderId-cell";
import OrderTableRushCell from "../../../../../components/order-table/rush-cell";
import OrderTableStatusCell from "../../../../../components/order-table/status-cell";
import TableHeaderDatePicker from "../../../../../components/table-header-datepicker/table-header-datepicker";
import TableHeaderSelectField from "../../../../../components/table-header-select-field/table-header-select-field";
import TableHeaderTextField from "../../../../../components/table-header-text-filed/table-header-text-filed";
import { getInspectionServicesRequest } from "../../../../../redux/inspection-firms/slice";
import {
  getAllOrdersSuccess,
  getAllOrdersRequest,
} from "../../../../../redux/orders/slice";
import Table from "../../../../../components/table/table";
import { deleteOrderRequest } from "../../../../../utils/APICalls";
import { orderStatus } from "../../../../../utils/Constant";
import DeleteOrderDialog from "../../../../../components/delete-order-dialog/delete-order-dialog";
import ErrorNotification from "../../../../../components/error-notification/error-notification";
import Loading from "../../../../../components/loading/loading";
import Notification from "../../../../../components/notification/notification";

export default function InspectionFirmOrders({ data, isMyOrganisation }: any) {
  const dispatch = useDispatch();
  const [display, setDisplay] = useState("all");
  const [rowData, setRowData] = useState<any>([]);
  const [showTableFilter, setShowTableFilter] = useState(true);
  const [headerHeight, setHeaderHeight] = useState(104);
  const [paginationModel, setPaginationModel] = useState<any>();
  const [filterParams, setFilterParams] = useState({});
  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>([]);
  const [selectedRowData, setSelectedRowData] = useState<any[]>([]);
  const [inspectionType, setInspectionType] = useState<any[]>([]);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [showLoading, setShowLoading] = useState(false);
  const [filterResetKey, setFilterResetKey] = useState(0);

  const { loading, allOrders } = useSelector((state: any) => state.orders);
  const { inspectionServices } = useSelector(
    (state: any) => state.inspectionFirms
  );

  const rowCountRef = useRef(allOrders?.totalCount || 0);
  const rowCount = useMemo(() => {
    if (allOrders?.totalCount !== undefined) {
      rowCountRef.current = allOrders?.totalCount;
    }
    return rowCountRef.current;
  }, [allOrders?.totalCount]);

  const column: GridColDef[] = [
    {
      field: "flags",
      headerName: "Flags",
      width: 120,
      sortable: false,
      renderCell: (params: GridRenderCellParams<any>) => (
        <OrderTableFlagsCell rowData={params.row} />
      ),
      renderHeader: (params) => (
        <Typography sx={{ fontWeight: "bold" }}>Flags</Typography>
      ),
    },
    {
      field: "orderNumber",
      headerName: "Order ID",
      width: 160,
      sortable: false,
      renderCell: (params: GridRenderCellParams<any>) => (
        <OrderTableOrderIdCell rowData={params.row} />
      ),
      renderHeader: (params) => (
        <TableHeaderTextField
          field="orderNumber"
          sortField="orderid"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterUsers}
        />
      ),
    },
    {
      field: "orderStatus",
      headerName: "Status",
      width: 160,
      sortable: false,
      renderCell: (params: GridRenderCellParams<any>) => (
        <OrderTableStatusCell rowData={params.row} />
      ),
      renderHeader: (params) => (
        <TableHeaderSelectField
          field="orderStatus"
          sortField="status"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterUsers}
          values={
            isMyOrganisation
              ? [
                  "InspectorAssigned",
                  "NoAppointment",
                  "FutureAppointment",
                  "PastAppointment",
                  "InspectorReturn",
                  "Submitted",
                  "OnHold",
                  "Closed",
                ]
              : [
                  "OrderAdded",
                  "OrderReview",
                  "OrderPending",
                  "AutoValidation",
                  "ManualValidation",
                  "AutoAssign",
                  "ManualAssign",
                  "Recruiting",
                  "InspectorAssigned",
                  "NoAppointment",
                  "FutureAppointment",
                  "PastAppointment",
                  "InspectorReturn",
                  "UnassignedQC",
                  "AssignedQC",
                  "FinalQC",
                  "ResultsDelivered",
                  "ToBeInvoiced",
                  "Invoiced",
                  "ClientReturns",
                  "OnHold",
                  "Closed",
                ]
          }
        />
      ),
    },
    {
      field: "isRushInspection",
      headerName: "Rush?",
      width: 110,
      sortable: false,
      renderCell: (params: GridRenderCellParams<any>) => (
        <OrderTableRushCell rowData={params.row} />
      ),
      renderHeader: (params) => (
        <TableHeaderSelectField
          field="isRushInspection"
          sortField="isrushinspection"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterUsers}
          values={["Yes", "No"]}
        />
      ),
    },
    {
      field: "notes",
      headerName: "Quick Note",
      width: 180,
      sortable: false,
      valueGetter: (value, row) => {
        return row.notes && row.notes.length > 0 ? row.notes[0].note : "";
      },
      renderHeader: (params) => (
        <TableHeaderTextField
          field="note"
          sortField="note"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterUsers}
        />
      ),
    },
    {
      field: "client",
      headerName: "Client",
      width: 180,
      sortable: false,
      renderCell: (params: GridRenderCellParams<any>) => (
        <OrderTableClientCell rowData={params.row} />
      ),
      renderHeader: (params) => (
        <TableHeaderTextField
          field="clientName"
          sortField="clientname"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterUsers}
        />
      ),
    },
    {
      field: "inspectionType",
      headerName: "Inspection Type",
      width: 180,
      sortable: false,
      renderHeader: (params) => (
        <TableHeaderSelectField
          field="inspectionType"
          sortField="inspectiontype"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterUsers}
          values={inspectionType}
        />
      ),
    },
    {
      field: "clientDueDate",
      headerName: "Client Due Date",
      width: 180,
      sortable: false,
      valueFormatter: (value?: string) =>
        value ? dayjs(value).format("MM/DD/YYYY") : " ",
      renderHeader: (params) => (
        <TableHeaderDatePicker
          field="clientDueDate"
          sortField="clientduedate"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterUsers}
        />
      ),
    },
    {
      field: "eta",
      headerName: "ETA",
      width: 100,
      sortable: false,
      renderHeader: (params) => (
        <Typography sx={{ fontWeight: "bold" }}>ETA</Typography>
      ),
    },
    {
      field: "address",
      headerName: "Address",
      width: 180,
      sortable: false,
      valueGetter: (value, row) => {
        return row?.propertyRecord?.street || "";
      },
      renderHeader: (params) => (
        <TableHeaderTextField
          field="address"
          sortField="address"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterUsers}
        />
      ),
    },
    {
      field: "city",
      headerName: "City",
      width: 160,
      sortable: false,
      valueGetter: (value, row) => {
        return row?.propertyRecord?.city || "";
      },
      renderHeader: (params) => (
        <TableHeaderTextField
          field="city"
          sortField="city"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterUsers}
        />
      ),
    },
    {
      field: "state",
      headerName: "State",
      width: 160,
      sortable: false,
      valueGetter: (value, row) => {
        return row?.propertyRecord?.state || "";
      },
      renderHeader: (params) => (
        <TableHeaderTextField
          field="state"
          sortField="state"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterUsers}
        />
      ),
    },
    {
      field: "zip",
      headerName: "Zip",
      width: 160,
      sortable: false,
      valueGetter: (value, row) => {
        return row?.propertyRecord?.zip || "";
      },
      renderHeader: (params) => (
        <TableHeaderTextField
          field="zip"
          sortField="zip"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterUsers}
        />
      ),
    },
    {
      field: "propertyContactName",
      headerName: "Property Contact Name",
      width: 240,
      sortable: false,
      valueGetter: (value, row) => {
        return row.propertyContacts && row.propertyContacts.length > 0
          ? row.propertyContacts[0].name
          : "";
      },
      renderHeader: (params) => (
        <TableHeaderTextField
          field="propertyContactName"
          sortField="propertycontactname"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterUsers}
        />
      ),
    },
    {
      field: "propertyContactPhone",
      headerName: "Property Contact Phone",
      width: 240,
      sortable: false,
      valueGetter: (value, row) => {
        return row.propertyContacts && row.propertyContacts.length > 0
          ? row.propertyContacts[0].phone
          : "";
      },
      renderHeader: (params) => (
        <TableHeaderTextField
          field="propertyContactPhone"
          sortField="propertycontactphone"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterUsers}
        />
      ),
    },
    {
      field: "daysInQueue",
      headerName: "Days In Queue",
      width: 180,
      sortable: false,
      renderHeader: (params) => (
        <Typography sx={{ fontWeight: "bold" }}>Days In Queue</Typography>
      ),
    },
  ];

  useEffect(() => {
    setPaginationModel({
      page: 0,
      pageSize: 10,
    });
    if (inspectionServices.length === 0)
      dispatch(getInspectionServicesRequest());
    dispatch(getAllOrdersSuccess({ response: null }));
    if (isMyOrganisation) {
      setFilterParams({ ...filterParams });
    } else {
      setFilterParams({ ...filterParams, inspectorFirmId: data?.id });
    }
  }, [data]); // eslint-disable-line

  useEffect(() => {
    if (inspectionServices && inspectionServices.length > 0) {
      const type = inspectionServices.reduce((acc: any[], item: any) => {
        if (!acc.includes(item.inspectionType)) {
          acc.push(item.inspectionType);
        }
        return acc;
      }, []);
      setInspectionType(type);
    }
  }, [inspectionServices]);

  useEffect(() => {
    setRowData(allOrders?.items);
  }, [allOrders]);

  useEffect(() => {
    if (paginationModel) {
      const pagination = { ...paginationModel };
      pagination.page = paginationModel?.page + 1;
      setFilterParams({ ...filterParams, ...pagination });
    }
  }, [paginationModel]); // eslint-disable-line

  useEffect(() => {
    //Removing filterParams object key if their value is empty
    const filters = filterParams as any;
    Object.keys(filterParams)?.forEach((key: string) => {
      if (filters.hasOwnProperty(key) && filters[key] === "") {
        delete filters[key];
      }
    });
    if (Object.keys(filters).length > 0) {
      dispatch(getAllOrdersRequest(filters));
    }
  }, [filterParams]); // eslint-disable-line

  const handleDeleteClick = () => {
    if (rowSelectionModel.length === 0) return;
    setShowDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    if (rowSelectionModel.length === 0) return;

    setShowLoading(true);
    const orderIds = rowSelectionModel as string[];

    // Delete all selected orders
    const response = await deleteOrderRequest(orderIds);
    setShowLoading(false);

    if (response?.status === 200 || response?.status === 204) {
      setSuccessMessage(
        orderIds.length === 1
          ? "Order deleted successfully."
          : `${orderIds.length} orders deleted successfully.`
      );
      setShowSuccessNotification(true);
      setRowSelectionModel([]);
      setShowDeleteDialog(false);
      // Refresh the orders list
      dispatch(getAllOrdersRequest(filterParams));
    } else {
      setErrorMessage("Failed to delete orders. Please try again.");
      setShowErrorNotification(true);
      setShowDeleteDialog(false);
    }
  };

  const onFilterClick = (value: boolean) => {
    setShowTableFilter(value);
    setHeaderHeight(value ? 104 : 56);
  };

  const onFilterUsers = (data: any) => {
    const keys = Object.keys(data);
    if (keys.includes("sortColumn")) {
      setFilterParams({ ...filterParams, ...data });
    } else {
      keys.forEach((key: string) => {
        if (filterParams.hasOwnProperty(key) && data[key] === "") {
          const { [key]: removedKey, ...filters } = filterParams as any;
          setFilterParams(filters);
        } else if (data[key] || data[key] === 0 || data[key] === false) {
          //if isActive status is false and if userType is 0 then condition also should pass
          setFilterParams({ ...filterParams, [key]: data[key] });
        }
      });
    }
  };

  const onDisplayChange = (event: SelectChangeEvent) => {
    const value = event.target.value;
    setDisplay(value);
    if (value !== "all") {
      setFilterParams({ ...filterParams, queueType: event.target.value });
    } else {
      if (filterParams.hasOwnProperty("queueType")) {
        const { queueType, ...filters } = filterParams as any;
        setFilterParams(filters);
      }
    }
  };

  const onClearAllFilters = () => {
    setDisplay("all");
    setPaginationModel({
      page: 0,
      pageSize: 10,
    });
    if (isMyOrganisation) {
      setFilterParams({
        page: 1,
        pageSize: 10,
      } as any);
    } else {
      setFilterParams({
        inspectorFirmId: data?.id,
        page: 1,
        pageSize: 10,
      } as any);
    }
    setFilterResetKey((prev) => prev + 1);
  };

  const checkDeleteAllowed = () => {
    const belowPastAppointmentStatus = selectedRowData.filter(
      (data: any) =>
        orderStatus?.[data?.orderStatus as keyof typeof orderStatus] < 43
    );
    return belowPastAppointmentStatus.length === selectedRowData.length
      ? false
      : true;
  };

  return (
    <>
      <Typography sx={{ ml: 2, mt: 2 }} className="title">
        Orders
      </Typography>
      <FormControl sx={{ ml: 2, mt: 4, width: 250 }}>
        <InputLabel id="display-select-label">Display</InputLabel>
        {isMyOrganisation ? (
          <Select
            labelId="display-select-label"
            id="display-select"
            value={display}
            label="Display"
            onChange={onDisplayChange}
          >
            <MenuItem value="all">All Orders</MenuItem>
            <MenuItem value="Assign">Assign</MenuItem>
            <MenuItem value="Accepted">Accepted</MenuItem>
            <MenuItem value="Submitted">Submitted</MenuItem>
            <MenuItem value="OnHold">On Hold</MenuItem>
            <MenuItem value="Closed">Closed</MenuItem>
          </Select>
        ) : (
          <Select
            labelId="display-select-label"
            id="display-select"
            value={display}
            label="Display"
            onChange={onDisplayChange}
          >
            <MenuItem value="all">All Orders</MenuItem>
            <MenuItem value="Created">Created</MenuItem>
            <MenuItem value="Validation">Validation</MenuItem>
            <MenuItem value="Assign">Assign</MenuItem>
            <MenuItem value="Accepted">Accepted</MenuItem>
            <MenuItem value="Qc">QC Review</MenuItem>
            <MenuItem value="Billing">Billing</MenuItem>
            <MenuItem value="OnHold">On Hold</MenuItem>
            <MenuItem value="Closed">Closed</MenuItem>
          </Select>
        )}
      </FormControl>

      <Box
        className="clients-header"
        sx={{
          mt: 3,
          mb: 1.5,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {isMyOrganisation ? (
          <Box></Box>
        ) : (
          <Box display={"flex"} alignItems={"center"}>
            <IconButton
              aria-label="delete"
              disabled={rowSelectionModel.length === 0 || checkDeleteAllowed()}
              onClick={handleDeleteClick}
            >
              <DeleteOutlinedIcon
                color={
                  rowSelectionModel.length === 0 || checkDeleteAllowed()
                    ? "disabled"
                    : "error"
                }
              />
            </IconButton>
          </Box>
        )}
        <Box>
          <IconButton aria-label="clear-all" onClick={onClearAllFilters}>
            <ClearAllOutlined color="primary" />
          </IconButton>
          <IconButton
            aria-label="filter"
            onClick={() => onFilterClick(!showTableFilter)}
          >
            {showTableFilter ? (
              <FilterAltOffOutlinedIcon color="primary" />
            ) : (
              <FilterAltOutlinedIcon color="primary" />
            )}
          </IconButton>
        </Box>
      </Box>

      <Box sx={{ mx: 2 }}>
        <Table
          key={filterResetKey}
          rowData={rowData}
          rowCount={rowCount}
          loading={loading}
          columnDef={column}
          headerHeight={headerHeight}
          disableVirtualization={true}
          disableColumnMenu={true}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          checkboxSelection={isMyOrganisation ? false : true}
          onRowSelectionModelChange={(newRowSelectionModel: any) => {
            setRowSelectionModel(newRowSelectionModel);
            const selected = newRowSelectionModel.map((id: string) =>
              rowData.find((r: any) => r.id === id)
            );
            setSelectedRowData(selected);
          }}
          rowSelectionModel={rowSelectionModel}
        />
      </Box>

      {/* Delete Confirmation Dialog */}
      {showDeleteDialog && (
        <DeleteOrderDialog
          open={showDeleteDialog}
          onClose={() => setShowDeleteDialog(false)}
          onConfirm={handleDeleteConfirm}
          count={rowSelectionModel.length}
          isProcessing={showLoading}
        />
      )}

      {/* Success Notification */}
      {showSuccessNotification && (
        <Notification
          show={showSuccessNotification}
          onClose={() => setShowSuccessNotification(false)}
          title="Success"
          message={successMessage}
        />
      )}

      {/* Error Notification */}
      {showErrorNotification && (
        <ErrorNotification
          show={showErrorNotification}
          onClose={() => setShowErrorNotification(false)}
          title="Error"
          message={errorMessage}
        />
      )}

      {showLoading && <Loading />}
    </>
  );
}
